import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/apiClient.js';
import { useApi } from '../../lib/useApi.js';
import { toast } from '../../lib/useToast.js';
import {
  BrandedEmptyState,
  ErrorState,
  LoadingSkeleton,
  PageHeader,
  Select,
  StatusChip,
} from '../../components/index.js';
import './guideResultsWorkspace.css';

const SEVERITY_RANK = { critical: 0, major: 1, minor: 2, cosmetic: 3 };
const DEFAULT_OWNER_SELECTION = Object.freeze({ mode: 'all_except', ids: [] });

export function normalizeGuideResultOwnerSelection(preferences = {}) {
  const mode = preferences.guideResultsUserFilterMode === 'only' ? 'only' : 'all_except';
  const rawIds = Array.isArray(preferences.guideResultsUserFilterIds)
    ? preferences.guideResultsUserFilterIds
    : [];
  const ids = [...new Set(rawIds.map((value) => String(value || '').trim()).filter(Boolean))];
  return { mode, ids };
}

export function isGuideResultOwnerSelected(ownerUserId, selection = DEFAULT_OWNER_SELECTION) {
  const selectedIds = new Set(selection.ids || []);
  return selection.mode === 'only' ? selectedIds.has(ownerUserId) : !selectedIds.has(ownerUserId);
}

function withOwnerToggled(selection, ownerUserId) {
  const ids = new Set(selection.ids || []);
  const selected = isGuideResultOwnerSelected(ownerUserId, selection);
  if (selection.mode === 'only') {
    if (selected) ids.delete(ownerUserId);
    else ids.add(ownerUserId);
  } else if (selected) ids.add(ownerUserId);
  else ids.delete(ownerUserId);
  return { mode: selection.mode, ids: [...ids] };
}

function withOwnerIncluded(selection, ownerUserId) {
  if (isGuideResultOwnerSelected(ownerUserId, selection)) return selection;
  const ids = new Set(selection.ids || []);
  if (selection.mode === 'only') ids.add(ownerUserId);
  else ids.delete(ownerUserId);
  return { mode: selection.mode, ids: [...ids] };
}

function date(value, fallback = 'Not recorded') {
  if (!value) return fallback;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? fallback : parsed.toLocaleString();
}

export function freshness(attempt) {
  if (attempt.mirrorStatus === 'pending') return 'Pending synchronization';
  if (!attempt.mirroredAt)
    return attempt.environmentId === 'pmhub'
      ? 'Local authoritative result'
      : 'Mirror timestamp unavailable';
  const lag = new Date(attempt.mirroredAt).getTime() - new Date(attempt.updatedAt).getTime();
  if (!Number.isFinite(lag) || lag < 0) return 'Freshness unknown';
  return lag > 5 * 60_000 ? `Stale by ${Math.ceil(lag / 60_000)} minutes` : 'Synchronized';
}

// Exception-first reading order: failed cards (severity-ranked) → blocked cards
// → otherwise-terminal cards that still carry reviewer signal (severity, notes,
// or a feedback-link exception). Everything else is collapsed pass noise.
export function deriveFindings(state) {
  const entries = Object.entries(state?.cases || {});
  const findings = entries
    .map(([cardId, card]) => {
      const noted = Boolean(
        card.severity || (card.notes && String(card.notes).trim()) || card.feedbackLinkException,
      );
      if (card.status === 'fail') return { cardId, card, kind: 'fail' };
      if (card.status === 'blocked') return { cardId, card, kind: 'blocked' };
      if (noted) return { cardId, card, kind: 'noted' };
      return null;
    })
    .filter(Boolean);
  const kindRank = { fail: 0, blocked: 1, noted: 2 };
  findings.sort((left, right) => {
    if (kindRank[left.kind] !== kindRank[right.kind])
      return kindRank[left.kind] - kindRank[right.kind];
    const leftSeverity = SEVERITY_RANK[left.card.severity] ?? 9;
    const rightSeverity = SEVERITY_RANK[right.card.severity] ?? 9;
    if (leftSeverity !== rightSeverity) return leftSeverity - rightSeverity;
    return left.cardId.localeCompare(right.cardId);
  });
  return findings;
}

function findingLabel(kind) {
  if (kind === 'fail') return 'Fail';
  if (kind === 'blocked') return 'Blocked';
  return 'Note';
}

function FindingCard({ cardId, card, kind }) {
  return (
    <article className={`grw-finding grw-finding--${kind}`} data-testid="grw-finding">
      <div className="row spread">
        <strong>{cardId}</strong>
        <span className="grw-finding-kind">
          <StatusChip status={card.status || 'not-started'} />
          {card.severity ? (
            <em className={`grw-severity grw-severity--${card.severity}`}>{card.severity}</em>
          ) : null}
        </span>
      </div>
      {card.notes ? <p className="grw-finding-notes">{card.notes}</p> : null}
      {card.feedbackIds?.length ? (
        <p className="grw-finding-links">
          Feedback:{' '}
          {card.feedbackIds.map((feedbackId, index) => (
            <span key={feedbackId}>
              {index ? ', ' : ''}
              <Link to={`/feedback/queue?record=${encodeURIComponent(feedbackId)}`}>
                {feedbackId}
              </Link>
            </span>
          ))}
        </p>
      ) : null}
      {card.feedbackLinkException ? (
        <p className="grw-finding-exception">Feedback exception: {card.feedbackLinkException}</p>
      ) : null}
      {card.firstRecordedAt ? (
        <small className="muted">First recorded: {date(card.firstRecordedAt, 'Unknown')}</small>
      ) : null}
      <span className="visually-hidden">{findingLabel(kind)}</span>
    </article>
  );
}

function OwnerFilter({
  owners,
  selection,
  onToggle,
  onSelectAll,
  onClearAll,
  saveStatus,
  onRetrySave,
  onRetryLoad,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const selectedCount = owners.filter((owner) =>
    isGuideResultOwnerSelected(owner.id, selection),
  ).length;
  const summary =
    selectedCount === 0
      ? 'None'
      : selectedCount === owners.length
        ? 'All'
        : `${selectedCount} of ${owners.length}`;
  const visibleOwners = owners.filter((owner) =>
    `${owner.displayName} ${owner.id}`.toLowerCase().includes(query.trim().toLowerCase()),
  );

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    searchRef.current?.focus();
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="grw-filter grw-owner-filter" ref={rootRef}>
      <span className="grw-filter-label">Users</span>
      <button
        ref={triggerRef}
        type="button"
        className="form-input grw-owner-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span>Users: {summary}</span>
        <span aria-hidden>▾</span>
      </button>
      {open ? (
        <div className="grw-owner-popover" role="dialog" aria-label="Filter guide results by user">
          <div className="grw-owner-popover-head">
            <strong>Select users</strong>
            <span>{selectedCount} selected</span>
          </div>
          <input
            ref={searchRef}
            type="search"
            className="form-input"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search users"
            aria-label="Search guide result users"
          />
          <div className="grw-owner-actions">
            <button type="button" className="btn ghost sm" onClick={onSelectAll}>
              Select all users
            </button>
            <button type="button" className="btn ghost sm" onClick={onClearAll}>
              Clear all users
            </button>
          </div>
          <div className="grw-owner-options" role="group" aria-label="Guide result users">
            {visibleOwners.map((owner) => (
              <label key={owner.id} className="grw-owner-option">
                <input
                  type="checkbox"
                  aria-label={owner.label}
                  checked={isGuideResultOwnerSelected(owner.id, selection)}
                  onChange={() => onToggle(owner.id)}
                />
                <span>
                  <strong>{owner.label}</strong>
                  <small>{owner.id}</small>
                </span>
              </label>
            ))}
            {!visibleOwners.length ? <p className="muted">No users match this search.</p> : null}
          </div>
          <div className="grw-owner-save" aria-live="polite">
            {saveStatus === 'saving' ? <span>Saving…</span> : null}
            {saveStatus === 'saved' ? <span className="is-saved">Saved</span> : null}
            {saveStatus === 'error' ? (
              <>
                <span className="is-error">Not saved</span>
                <button type="button" className="btn ghost sm" onClick={onRetrySave}>
                  Retry saving user filter
                </button>
              </>
            ) : null}
            {saveStatus === 'load-error' ? (
              <>
                <span className="is-error">Saved filter unavailable</span>
                <button type="button" className="btn ghost sm" onClick={onRetryLoad}>
                  Retry loading user filter
                </button>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function GuideResultsWorkspacePage() {
  const results = useApi('/admin/guide-results');
  const preferences = useApi('/me/preferences');
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('attempt') || null;
  const [filters, setFilters] = useState({
    environment: '',
    guide: '',
    status: '',
    attention: false,
  });
  const [detail, setDetail] = useState({ id: null, data: null, loading: false, error: null });
  const [activeTab, setActiveTab] = useState('findings');
  const [reviewPending, setReviewPending] = useState(false);
  const [ownerSelection, setOwnerSelection] = useState(DEFAULT_OWNER_SELECTION);
  const [preferenceReady, setPreferenceReady] = useState(false);
  const [preferenceStatus, setPreferenceStatus] = useState('loading');
  const saveChainRef = useRef(Promise.resolve());
  const saveVersionRef = useRef(0);
  const localSelectionChangedRef = useRef(false);

  const attempts = useMemo(() => results.data?.data || [], [results.data]);

  const environments = useMemo(
    () => [...new Set(attempts.map((attempt) => attempt.environmentId))].sort(),
    [attempts],
  );
  const guides = useMemo(
    () => [...new Set(attempts.map((attempt) => attempt.guideId))].sort(),
    [attempts],
  );
  const owners = useMemo(() => {
    const byId = new Map();
    for (const attempt of attempts) {
      if (!attempt.ownerUserId) continue;
      if (!byId.has(attempt.ownerUserId)) {
        byId.set(attempt.ownerUserId, {
          id: attempt.ownerUserId,
          displayName: attempt.ownerDisplayName || attempt.ownerUserId,
        });
      }
    }
    const values = [...byId.values()].sort(
      (left, right) =>
        left.displayName.localeCompare(right.displayName) || left.id.localeCompare(right.id),
    );
    const nameCounts = values.reduce((counts, owner) => {
      counts.set(owner.displayName, (counts.get(owner.displayName) || 0) + 1);
      return counts;
    }, new Map());
    return values.map((owner) => ({
      ...owner,
      label:
        nameCounts.get(owner.displayName) > 1
          ? `${owner.displayName} (${owner.id})`
          : owner.displayName,
    }));
  }, [attempts]);

  useEffect(() => {
    if (preferences.data && !localSelectionChangedRef.current) {
      const remote =
        preferences.data?.data && typeof preferences.data.data === 'object'
          ? preferences.data.data
          : {};
      setOwnerSelection(normalizeGuideResultOwnerSelection(remote));
      setPreferenceReady(true);
      setPreferenceStatus('idle');
    } else if (preferences.error) {
      setPreferenceReady(true);
      setPreferenceStatus((current) => (current === 'saving' ? current : 'load-error'));
    }
  }, [preferences.data, preferences.error]);

  function persistOwnerSelection(nextSelection) {
    const version = ++saveVersionRef.current;
    setPreferenceStatus('saving');
    const payload = {
      guideResultsUserFilterMode: nextSelection.mode,
      guideResultsUserFilterIds: nextSelection.ids,
    };
    const request = saveChainRef.current
      .catch(() => undefined)
      .then(() => api.patch('/me/preferences', payload));
    saveChainRef.current = request;
    request
      .then(() => {
        if (saveVersionRef.current === version) setPreferenceStatus('saved');
      })
      .catch(() => {
        if (saveVersionRef.current === version) setPreferenceStatus('error');
      });
  }

  function applyOwnerSelection(nextSelection) {
    localSelectionChangedRef.current = true;
    setOwnerSelection(nextSelection);
    setPreferenceReady(true);
    persistOwnerSelection(nextSelection);
    if (
      detail.data?.ownerUserId &&
      !isGuideResultOwnerSelected(detail.data.ownerUserId, nextSelection)
    ) {
      select(null);
    }
  }

  function toggleOwner(ownerUserId) {
    applyOwnerSelection(withOwnerToggled(ownerSelection, ownerUserId));
  }

  function includeOwner(ownerUserId) {
    applyOwnerSelection(withOwnerIncluded(ownerSelection, ownerUserId));
  }

  const filtered = useMemo(
    () =>
      attempts.filter(
        (attempt) =>
          (!filters.environment || attempt.environmentId === filters.environment) &&
          (!filters.guide || attempt.guideId === filters.guide) &&
          (!filters.status || attempt.status === filters.status) &&
          isGuideResultOwnerSelected(attempt.ownerUserId, ownerSelection) &&
          (!filters.attention ||
            (attempt.summary?.fail || 0) + (attempt.summary?.blocked || 0) > 0 ||
            attempt.reviewStatus !== 'reviewed'),
      ),
    [attempts, filters, ownerSelection],
  );

  const selectedOwnerCount = owners.filter((owner) =>
    isGuideResultOwnerSelected(owner.id, ownerSelection),
  ).length;
  const noOwnersSelected = owners.length > 0 && selectedOwnerCount === 0;

  const reviewedCount = filtered.filter((attempt) => attempt.reviewStatus === 'reviewed').length;
  const openFindings = filtered.reduce(
    (sum, attempt) => sum + (attempt.summary?.fail || 0) + (attempt.summary?.blocked || 0),
    0,
  );

  useEffect(() => {
    if (!selectedId) {
      setDetail({ id: null, data: null, loading: false, error: null });
      return undefined;
    }
    let cancelled = false;
    setActiveTab('findings');
    setDetail({ id: selectedId, data: null, loading: true, error: null });
    api
      .get(`/admin/guide-results/${selectedId}`)
      .then((response) => {
        if (!cancelled)
          setDetail({ id: selectedId, data: response.data, loading: false, error: null });
      })
      .catch((error) => {
        if (!cancelled) setDetail({ id: selectedId, data: null, loading: false, error });
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  function select(attemptId) {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (attemptId) next.set('attempt', attemptId);
        else next.delete('attempt');
        return next;
      },
      { replace: false },
    );
  }

  async function toggleReviewed() {
    if (!detail.data || reviewPending) return;
    const nextReviewed = detail.data.reviewStatus !== 'reviewed';
    setReviewPending(true);
    try {
      const response = await api.post(`/admin/guide-results/${detail.data.id}/review`, {
        reviewed: nextReviewed,
      });
      setDetail((current) => ({ ...current, data: { ...current.data, ...response.data } }));
      results.refresh();
      toast.success({
        title: nextReviewed ? 'Marked reviewed' : 'Marked unreviewed',
        message: `${detail.data.ownerDisplayName} · Guide ${detail.data.guideId} · Attempt ${detail.data.attemptNumber}`,
      });
    } catch (error) {
      toast.error({ title: 'Review update failed', message: error?.message || 'Try again.' });
    } finally {
      setReviewPending(false);
    }
  }

  function goToNextUnreviewed() {
    if (!filtered.length) return;
    const currentIndex = filtered.findIndex((attempt) => attempt.id === selectedId);
    for (let step = 1; step <= filtered.length; step += 1) {
      const candidate = filtered[(currentIndex + step + filtered.length) % filtered.length];
      if (candidate.reviewStatus !== 'reviewed' && candidate.id !== selectedId) {
        select(candidate.id);
        return;
      }
    }
    toast.success({ title: 'All caught up', message: 'Every submission in view is reviewed.' });
  }

  function goToPrevious() {
    const currentIndex = filtered.findIndex((attempt) => attempt.id === selectedId);
    if (currentIndex > 0) select(filtered[currentIndex - 1].id);
  }

  const exportHref = useMemo(() => {
    const query = new URLSearchParams();
    if (filters.environment) query.set('environmentId', filters.environment);
    if (filters.guide) query.set('guideId', filters.guide);
    if (filters.status) query.set('status', filters.status);
    query.set('ownerSelectionMode', ownerSelection.mode);
    ownerSelection.ids.forEach((ownerUserId) => query.append('ownerUserId', ownerUserId));
    const suffix = query.toString();
    return `/api/admin/guide-results/export${suffix ? `?${suffix}` : ''}`;
  }, [filters, ownerSelection]);

  const selected = detail.data;
  const findings = useMemo(() => (selected ? deriveFindings(selected.state) : []), [selected]);
  const acknowledgements = useMemo(
    () => Object.entries(selected?.state?.acknowledgements || {}),
    [selected],
  );
  const acknowledgedCount = acknowledgements.filter(([, value]) => value).length;
  const recordedCards = useMemo(() => Object.entries(selected?.state?.cases || {}), [selected]);
  const summary = selected?.summary || {};
  const notStarted = Math.max(0, (summary.total || 0) - (summary.completed || 0));
  const temporaryOwnerException = Boolean(
    selected?.ownerUserId && !isGuideResultOwnerSelected(selected.ownerUserId, ownerSelection),
  );

  const tabs = selected
    ? [
        { id: 'findings', label: `Findings (${findings.length})` },
        { id: 'cards', label: `Recorded cards (${recordedCards.length})` },
        // Guides without an acknowledgement checklist skip the tab entirely —
        // an "Acknowledgements (0/0)" tab reads like missing data.
        ...(acknowledgements.length
          ? [
              {
                id: 'acks',
                label: `Acknowledgements (${acknowledgedCount}/${acknowledgements.length})`,
              },
            ]
          : []),
        { id: 'sync', label: 'Sync & evidence' },
      ]
    : [];

  return (
    <div className="guide-review-workspace" data-testid="guide-results-workspace">
      <PageHeader
        eyebrow="Administration"
        title="PM Guide Results"
        subtitle="Review external tester guide submissions — exceptions first, with evidence links and a durable review trail."
        actions={
          <div className="row" style={{ gap: 8 }}>
            <button type="button" className="btn ghost sm" onClick={() => results.refresh()}>
              Refresh
            </button>
            {noOwnersSelected ? (
              <button type="button" className="btn primary sm" disabled>
                Export ZIP
              </button>
            ) : (
              <a className="btn primary sm" href={exportHref}>
                Export ZIP
              </a>
            )}
          </div>
        }
      />
      {results.loading || (!preferenceReady && !results.error) ? (
        <LoadingSkeleton rows={6} />
      ) : null}
      {results.error ? <ErrorState error={results.error} onRetry={results.refresh} /> : null}
      {!results.loading && preferenceReady && !results.error && attempts.length === 0 ? (
        <BrandedEmptyState
          title="No guide submissions yet"
          description="Synchronized tester attempts will appear here once sandbox testers save or submit a PM guide."
        />
      ) : null}
      {preferenceReady && attempts.length ? (
        <div className="grw-layout">
          <aside className="grw-list-panel" aria-label="Submission list">
            <div className="grw-filters">
              <OwnerFilter
                owners={owners}
                selection={ownerSelection}
                onToggle={toggleOwner}
                onSelectAll={() => applyOwnerSelection({ mode: 'all_except', ids: [] })}
                onClearAll={() => applyOwnerSelection({ mode: 'only', ids: [] })}
                saveStatus={preferenceStatus}
                onRetrySave={() => persistOwnerSelection(ownerSelection)}
                onRetryLoad={preferences.refresh}
              />
              <div className="grw-filter">
                <label htmlFor="grw-filter-environment">Environment</label>
                <Select
                  id="grw-filter-environment"
                  value={filters.environment}
                  onChange={(value) =>
                    setFilters((current) => ({ ...current, environment: value }))
                  }
                  options={[
                    { value: '', label: 'All environments' },
                    ...environments.map((environment) => ({
                      value: environment,
                      label: environment,
                    })),
                  ]}
                />
              </div>
              <div className="grw-filter">
                <label htmlFor="grw-filter-guide">Guide</label>
                <Select
                  id="grw-filter-guide"
                  value={filters.guide}
                  onChange={(value) => setFilters((current) => ({ ...current, guide: value }))}
                  options={[
                    { value: '', label: 'All guides' },
                    ...guides.map((guide) => ({ value: guide, label: `Guide ${guide}` })),
                  ]}
                />
              </div>
              <div className="grw-filter">
                <label htmlFor="grw-filter-status">Status</label>
                <Select
                  id="grw-filter-status"
                  value={filters.status}
                  onChange={(value) => setFilters((current) => ({ ...current, status: value }))}
                  options={[
                    { value: '', label: 'All statuses' },
                    { value: 'submitted', label: 'Submitted' },
                    { value: 'draft', label: 'Draft' },
                  ]}
                />
              </div>
              <label className="grw-filter grw-filter--toggle">
                <input
                  type="checkbox"
                  checked={filters.attention}
                  onChange={(event) =>
                    setFilters((current) => ({ ...current, attention: event.target.checked }))
                  }
                />
                <span>Needs attention</span>
              </label>
            </div>
            <p className="grw-progress" aria-live="polite">
              <strong>
                {reviewedCount} of {filtered.length} reviewed
              </strong>
              {openFindings ? <span> · {openFindings} open findings</span> : null}
            </p>
            <ul className="grw-list" aria-label="Guide submissions">
              {filtered.map((attempt) => {
                const findingCount = (attempt.summary?.fail || 0) + (attempt.summary?.blocked || 0);
                return (
                  <li key={attempt.id}>
                    <button
                      type="button"
                      className={`grw-list-item${attempt.id === selectedId ? ' is-selected' : ''}`}
                      data-attempt-id={attempt.id}
                      onClick={() => select(attempt.id)}
                    >
                      <span className="grw-list-top">
                        <strong>{attempt.ownerDisplayName}</strong>
                        <StatusChip status={attempt.status} />
                      </span>
                      <span className="grw-list-mid">
                        Guide {attempt.guideId} · Attempt {attempt.attemptNumber} ·{' '}
                        {attempt.environmentId}
                      </span>
                      <span className="grw-list-bottom">
                        <span>
                          {attempt.summary?.completed || 0}/{attempt.summary?.total || 0} cards
                        </span>
                        {findingCount ? (
                          <span className="grw-badge grw-badge--attention">
                            {findingCount} finding{findingCount === 1 ? '' : 's'}
                          </span>
                        ) : (
                          <span className="grw-badge">clean</span>
                        )}
                        {attempt.reviewStatus === 'reviewed' ? (
                          <span className="grw-badge grw-badge--reviewed">✓ reviewed</span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {noOwnersSelected ? (
              <div className="grw-list-empty grw-list-empty--owners">
                <strong>No users selected</strong>
                <span>Select at least one user to review submissions or export results.</span>
              </div>
            ) : !filtered.length ? (
              <p className="muted grw-list-empty">No submissions match the current filters.</p>
            ) : null}
          </aside>
          <section className="grw-pane" aria-label="Submission detail">
            {!selectedId ? (
              <BrandedEmptyState
                title="Select a submission"
                description="Pick a tester submission on the left to read its findings, cards, and acknowledgements."
              />
            ) : null}
            {detail.loading ? <LoadingSkeleton rows={6} /> : null}
            {detail.error ? <ErrorState error={detail.error} /> : null}
            {selected && !detail.loading ? (
              <>
                {temporaryOwnerException ? (
                  <div className="banner amber grw-owner-exception" role="status">
                    <div>
                      <strong>Outside your saved user filter</strong>
                      <p>
                        This direct link opened {selected.ownerDisplayName}&apos;s submission as a
                        temporary exception. It is not included in counts, export, or navigation.
                      </p>
                    </div>
                    <div className="row wrap grw-owner-exception-actions" style={{ gap: 8 }}>
                      <button
                        type="button"
                        className="btn primary sm"
                        onClick={() => includeOwner(selected.ownerUserId)}
                      >
                        Include {selected.ownerDisplayName}
                      </button>
                      <button type="button" className="btn ghost sm" onClick={() => select(null)}>
                        Close
                      </button>
                    </div>
                  </div>
                ) : null}
                <div className="grw-pane-header">
                  <div>
                    <h3>
                      {selected.ownerDisplayName} · Guide {selected.guideId} · Attempt{' '}
                      {selected.attemptNumber}
                    </h3>
                    <p className="muted">
                      {selected.environmentId} · {selected.status} · revision {selected.revision} ·{' '}
                      {freshness(selected)}
                    </p>
                    {selected.reviewStatus === 'reviewed' ? (
                      <p className="grw-reviewed-line">
                        Reviewed by {selected.reviewedByDisplay || selected.reviewedBy} ·{' '}
                        {date(selected.reviewedAt)}
                      </p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className={`btn sm ${selected.reviewStatus === 'reviewed' ? 'ghost' : 'primary'}`}
                    onClick={toggleReviewed}
                    disabled={reviewPending}
                  >
                    {selected.reviewStatus === 'reviewed' ? 'Mark unreviewed' : 'Mark reviewed'}
                  </button>
                </div>
                <div className="grw-tabs" role="tablist" aria-label="Submission sections">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={activeTab === tab.id}
                      className={`grw-tab${activeTab === tab.id ? ' is-active' : ''}`}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                {activeTab === 'findings' ? (
                  <div className="grw-tab-panel" role="tabpanel">
                    {selected.state?.generalNotes ? (
                      <p className="grw-general-notes">
                        <strong>General notes</strong>
                        <span>{selected.state.generalNotes}</span>
                      </p>
                    ) : null}
                    {findings.length ? (
                      <div className="grw-findings">
                        {findings.map((finding) => (
                          <FindingCard key={finding.cardId} {...finding} />
                        ))}
                      </div>
                    ) : (
                      <BrandedEmptyState
                        title="No findings"
                        description="Every recorded card passed cleanly — nothing needs reviewer attention."
                      />
                    )}
                    <p className="grw-pass-strip">
                      {summary.pass || 0} passed · {summary.fail || 0} failed ·{' '}
                      {summary.blocked || 0} blocked · {summary.skipped || 0} skipped
                      {notStarted ? ` · ${notStarted} not started` : ''}
                    </p>
                  </div>
                ) : null}
                {activeTab === 'cards' ? (
                  <div className="grw-tab-panel grw-cards" role="tabpanel">
                    {recordedCards.map(([cardId, card]) => (
                      <article key={cardId} className="grw-card">
                        <div className="row spread">
                          <strong>{cardId}</strong>
                          <StatusChip status={card.status || 'not-started'} />
                        </div>
                        {card.severity ? (
                          <span className="muted">Severity: {card.severity}</span>
                        ) : null}
                        {card.notes ? <p>{card.notes}</p> : null}
                        {card.feedbackIds?.length ? (
                          <small>
                            Feedback:{' '}
                            {card.feedbackIds.map((feedbackId, index) => (
                              <span key={feedbackId}>
                                {index ? ', ' : ''}
                                <Link
                                  to={`/feedback/queue?record=${encodeURIComponent(feedbackId)}`}
                                >
                                  {feedbackId}
                                </Link>
                              </span>
                            ))}
                          </small>
                        ) : null}
                        {card.feedbackLinkException ? (
                          <small>Feedback exception: {card.feedbackLinkException}</small>
                        ) : null}
                      </article>
                    ))}
                    {!recordedCards.length ? (
                      <BrandedEmptyState
                        title="No cards recorded"
                        description="The tester has not recorded any card outcomes yet."
                      />
                    ) : null}
                  </div>
                ) : null}
                {activeTab === 'acks' ? (
                  <div className="grw-tab-panel" role="tabpanel">
                    {acknowledgements.length ? (
                      <ul className="grw-acks">
                        {acknowledgements.map(([id, acknowledged]) => (
                          <li key={id}>
                            <span>{id}</span>
                            <strong className={acknowledged ? 'is-complete' : 'is-pending'}>
                              {acknowledged ? 'Acknowledged' : 'Not acknowledged'}
                            </strong>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <BrandedEmptyState
                        title="No acknowledgements"
                        description="This guide has no acknowledgement checklist, or none were recorded."
                      />
                    )}
                  </div>
                ) : null}
                {activeTab === 'sync' ? (
                  <div className="grw-tab-panel" role="tabpanel">
                    <dl className="grw-sync">
                      <div>
                        <dt>Freshness</dt>
                        <dd>{freshness(selected)}</dd>
                      </div>
                      <div>
                        <dt>Mirror status</dt>
                        <dd>{selected.mirrorStatus || 'local'}</dd>
                      </div>
                      <div>
                        <dt>Mirrored</dt>
                        <dd>{date(selected.mirroredAt, 'Not synchronized')}</dd>
                      </div>
                      <div>
                        <dt>Updated</dt>
                        <dd>{date(selected.updatedAt)}</dd>
                      </div>
                      <div>
                        <dt>Submitted</dt>
                        <dd>{date(selected.submittedAt, 'Not submitted')}</dd>
                      </div>
                      <div>
                        <dt>Retention expires</dt>
                        <dd>{date(selected.expiresAt, 'Unknown')}</dd>
                      </div>
                    </dl>
                    {noOwnersSelected ? (
                      <button type="button" className="btn outline sm" disabled>
                        Export ZIP with this view&apos;s filters
                      </button>
                    ) : (
                      <a className="btn outline sm" href={exportHref}>
                        Export ZIP with this view&apos;s filters
                      </a>
                    )}
                  </div>
                ) : null}
                <div className="grw-pane-footer">
                  <button
                    type="button"
                    className="btn ghost sm"
                    onClick={goToPrevious}
                    disabled={filtered.findIndex((attempt) => attempt.id === selectedId) <= 0}
                  >
                    ← Previous
                  </button>
                  <button
                    type="button"
                    className="btn outline sm"
                    onClick={goToNextUnreviewed}
                    disabled={!filtered.length}
                  >
                    Next unreviewed →
                  </button>
                </div>
              </>
            ) : null}
          </section>
        </div>
      ) : null}
    </div>
  );
}
